import React, { useState, useEffect } from 'react'
import Loading from '../../Loading'
import { useTranslation } from 'react-i18next'

function DonutChartSelfComparison({
    percentage = 0.6,
    mainColor = '#FC4C5999',
    backColor = '#FD949B99',
    loading = false,
    loadingColor = '#5E639B',
    loadingSecondColor = '#5E639B',
    size = 120,
    showCheckIns = true,
    participation = false,
    total = 100,
    completed = 60,
    stroke = 12,
    addDot = false,
    dotPercentage = 0,
    textColor = '#FC4C59',
    additionalText = '',
    showPercentage = true,
}) {
    const { t } = useTranslation()
    const radius = size / 2 - stroke / 2
    const circumference = radius * 2 * Math.PI
    const [currentPercentage, setCurrentPercentage] = useState(percentage)
    const [currentCompleted, setCurrentCompleted] = useState(completed)

    useEffect(() => {
        setCurrentPercentage(percentage)
        setCurrentCompleted(completed)
    }, [percentage, completed])

    const angle = (dotPercentage * 360 - 90) * (Math.PI / 180)

    const lineLength = stroke * 1.2
    const dif = stroke

    const centerX = (size + dif * 2) / 2
    const centerY = (size + dif * 2) / 2

    const innerX = centerX + (radius - lineLength) * Math.cos(angle)
    const innerY = centerY + (radius - lineLength) * Math.sin(angle)
    const outerX = centerX + (radius + lineLength) * Math.cos(angle)
    const outerY = centerY + (radius + lineLength) * Math.sin(angle)

    return (
        <div
            style={{
                width: size,
                height: size,
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {loading ? (
                <div>
                    <Loading primaryColor={loadingColor} secondaryColor={loadingSecondColor} size={120} stroke={8} />
                </div>
            ) : (
                <div className="relative">
                    {/* Background circle */}
                    <svg width={`${size}px`} height={`${size}px`} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'scaleX(-1)', position: 'absolute', left: 0, top: 0 }}>
                        <circle
                            transform={`rotate(-90,${size / 2},${size / 2})`}
                            cx="50%"
                            cy="50%"
                            r={radius}
                            fill="transparent"
                            stroke={backColor}
                            strokeWidth={stroke}
                            strokeDasharray={circumference}
                            strokeDashoffset="0"
                        />
                    </svg>
                    {/* Foreground circle with transition */}
                    <svg width={`${size}px`} height={`${size}px`} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'scaleX(1)' }}>
                        <circle
                            transform={`rotate(-90,${size / 2},${size / 2})`}
                            cx="50%"
                            cy="50%"
                            r={radius}
                            fill="transparent"
                            stroke={mainColor}
                            strokeLinecap="round"
                            strokeWidth={stroke}
                            strokeDasharray={circumference}
                            strokeDashoffset={circumference * (1 - currentPercentage)}
                            style={{
                                transition: 'stroke-dashoffset 400ms ease-in-out',
                            }}
                        />
                    </svg>
                    {addDot && (
                        <svg
                            width={`${size + dif * 2}px`}
                            height={`${size + dif * 2}px`}
                            viewBox={`0 0 ${size + dif * 2} ${size + dif * 2}`}
                            style={{
                                transform: 'scaleX(1)',
                                position: 'absolute',
                                top: -dif,
                                left: -dif,
                                zIndex: 2,
                            }}
                        >
                            <line x1={innerX} y1={innerY} x2={outerX} y2={outerY} stroke={'#413F5E'} strokeWidth={1.7} strokeDasharray="3 3" />
                        </svg>
                    )}
                    <div style={{ height: size, width: size }} className={`absolute top-0 items-center flex flex-col justify-center`}>
                        {participation ? (
                            <>
                                {!!additionalText && (
                                    <p className="text-[18px]  font-bold" style={{ color: textColor }}>
                                        {additionalText}
                                    </p>
                                )}
                                <p className="text-[14px] leading-[20px]  font-semibold" style={{ color: textColor }}>
                                    {Math.round(currentCompleted)}
                                    {showPercentage ? '%' : ''}
                                </p>
                            </>
                        ) : (
                            <div className="items-center flex flex-col justify-center gap-[3px]">
                                <p className="text-[20px] leading-[25px] text-darkenPurple font-semibold">{showCheckIns ? total : `${currentCompleted}/${total}`}</p>
                                <p className="text-[12px] leading-[12px] text-mediumGray font-medium text-center max-w-[75px]">
                                    {showCheckIns ? t('Check-ins') : t(`completed activities`)}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default DonutChartSelfComparison
